import { ChatItem, ChatResponseOption } from '@akin-home/chat';
import { ensureError } from '@akin-home/errors';
import { PromiseResult } from '@akin-home/result';
import { UserModel } from '@akin-home/user';
import { API } from 'aws-amplify';

export * from './queryProactiveMessages';

const API_NAME = 'chatapi';

export type ChatInputType = 'text' | 'labeled_intent';

export interface SendMessageParams {
  message: string;

  message_type?: ChatInputType;

  /** The unique identifier for the user sending the message */
  username: string;

  use_generative_ai?: UserModel['use_generative_ai'];

  /** This will be undefined if it is the first message being sent */
  session_id?: string;

  /** Can pass the application id to override which version of pixi is being spoken to */
  application_id: number;

  context?: object;
}

export interface ChatApiFailResponse {
  error: any;
  success?: false;
}

export interface ChatApiSuccessResponse {
  success: string;
  url: string;
  session_id: string;
  body: ChatItem | ChatItem[];
  did_match?: boolean;
  matched?: [{ matched_by: string }];
  error: undefined;
}

export type ChatApiResponse = ChatApiFailResponse | ChatApiSuccessResponse;

export const chatResponseIsDirectMatch = (res: ChatApiSuccessResponse) => {
  return (
    res.did_match &&
    res.matched &&
    ['direct', 'labeled_intent'].includes(res.matched[0].matched_by)
  );
};

/** Posts a message to the chat server */
export const postChatMessage = (
  body: SendMessageParams
): Promise<ChatApiResponse> => API.post(API_NAME, '/message', { body });

export async function sendLabelledMessage(params: SendMessageParams) {
  postChatMessage({ ...params, message_type: 'labeled_intent' });
}

export type ChatAPIResponse = {
  application_id: number;
  date: string;
  session_id: string;
  username: string;
  input: { response: 'display'; source: 'user'; type: 'text'; value: string };
  output: ChatAPIOutput;
  commands?: any[];
};

export type ChatAPIOutput = {
  // corrections: []
  matched?: boolean;
  multiple_choice_options?: boolean;
  options?: ChatResponseOption[];
  // response:"display"
  timezone?: string;
  type: string;
  value?: string;
  image_url?: string;
};
export async function postPublicChatMessage(
  body: SendMessageParams
): PromiseResult<ChatAPIResponse> {
  try {
    const result = (await API.post(API_NAME, '/public/message', {
      body,
    })) as { data: ChatAPIResponse };

    return { success: true, result: result.data };
  } catch (ex) {
    const error = ensureError(ex);
    return { success: false, error };
  }
}
